import React from 'react';
import { statusTint } from '../../lib/theme';

// EntityCard — the one card recipe for the people-and-things pages
// (lists.md, aisle 4 pick B: Crew, Fleet, Storage, Active Moves).
//
//   ┌──────────────────────────────────────┐
//   │ (avatar)  Name                [pill] │  header
//   │           role / sub                 │
//   │ Phone ................ (828) 555-01 │  facts  (label · value rows)
//   │ Days .................. M T W T F    │
//   │ …children (anything page-specific)…  │
//   ├──────────────────────────────────────┤
//   │ [Edit] [Days off]        Schedule → │  footer (actions)
//   └──────────────────────────────────────┘
//
// 16px padding, 12px radius, slate hover, red ring when `selected` (same
// ring the dispatch board uses). Don't give a card its own hover colour.

export function EntityAvatar({ text, color = '#64748b', square = false, size = 'md', className = '' }) {
    const sz = size === 'lg' ? 'w-12 h-12 text-[15px]' : size === 'sm' ? 'w-8 h-8 text-[11px]' : 'w-10 h-10 text-[13px]';
    return (
        <div
            className={`${sz} ${square ? 'rounded-lg' : 'rounded-full'} flex items-center justify-center font-extrabold text-white flex-shrink-0 ${className}`}
            style={{ backgroundColor: color }}
            aria-hidden="true"
        >
            {text}
        </div>
    );
}

export const initialsOf = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');

export function EntityCard({
    avatar,           // <EntityAvatar/> or any node
    title,
    subtitle,
    badge,            // status pill (right of the header)
    facts = [],       // [{ label, value, mono }]
    footer,           // action row; rendered above a hairline
    selected = false,
    muted = false,    // e.g. inactive / closed → opacity
    onClick,
    className = '',
    children,
    ...rest
}) {
    const interactive = typeof onClick === 'function';
    return (
        <div
            onClick={onClick}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
            className={`bg-white rounded-xl border p-4 flex flex-col gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 ${
                selected ? 'border-red-500 ring-[3px] ring-red-500/15' : 'border-slate-200 hover:border-slate-400 hover:shadow-sm'
            } ${interactive ? 'cursor-pointer' : ''} ${muted ? 'opacity-70' : ''} ${className}`}
            {...rest}
        >
            <div className="flex items-center gap-3 min-w-0">
                {avatar}
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-bold text-slate-900 truncate leading-tight">{title}</div>
                    {subtitle && <div className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</div>}
                </div>
                {badge && <div className="flex-shrink-0">{badge}</div>}
            </div>
            {facts.length > 0 && (
                <div className="flex flex-col gap-1.5 text-[13px]">
                    {facts.map((f, i) => (
                        <div key={i} className="flex items-baseline justify-between gap-3 min-w-0">
                            <span className="text-slate-400 flex-shrink-0">{f.label}</span>
                            <span className={`text-slate-700 font-medium text-right truncate ${f.mono ? 'font-mono tabular-nums' : ''}`}>{f.value ?? '—'}</span>
                        </div>
                    ))}
                </div>
            )}
            {children}
            {footer && (
                <div className="flex items-center gap-2 pt-3 border-t border-slate-100 mt-auto" onClick={(e) => e.stopPropagation()}>
                    {footer}
                </div>
            )}
        </div>
    );
}

// Status pill for card headers and list rows — tinted, square-cornered
// (rounded-md), uppercase. Pass the status colour; tint comes from theme.js.
export function StatusPill({ label, color = '#64748b', bg, className = '' }) {
    return (
        <span className={`inline-flex items-center h-[22px] px-2 rounded-md text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap ${className}`}
            style={{ background: statusTint(color, bg), color }}>
            {label}
        </span>
    );
}

// The card grid every entity page uses.
export const cardGridCls = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';

export default EntityCard;
